"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AwardDataset = void 0;
const interfaces_1 = require("@build-5/interfaces");
const rxjs_1 = require("rxjs");
const Dataset_1 = require("../Dataset");
/**
 * Award HTTPS Dataset object
 */
class AwardDataset extends Dataset_1.DatasetClass {
    constructor() {
        super(...arguments);
        /**
         * Create Award
         *
         * @param req Use {@link Build5Request} with data based on {@link AwardCreateRequest}
         * @returns
         */
        this.create = (req) => this.sendRequest(interfaces_1.WEN_FUNC.createAward)(req);
        /**
         * Fund award with native or base token.
         *
         * @param req Use {@link Build5Request} with data based on {@link AwardFundRequest}
         * @returns
         */
        this.fund = (req) => this.sendRequest(interfaces_1.WEN_FUNC.fundAward)(req);
        /**
         * Reject award
         *
         * @param req Use {@link Build5Request} with data based on {@link AwardRejectRequest}
         * @returns
         */
        this.reject = (req) => this.sendRequest(interfaces_1.WEN_FUNC.rejectAward)(req);
        /**
         * Add owner of the award. This grants the ability to manage it.
         *
         * @param req Use {@link Build5Request} with data based on {@link AwardAddOwnerRequest}
         * @returns
         */
        this.addOwner = (req) => this.sendRequest(interfaces_1.WEN_FUNC.addOwnerAward)(req);
        /**
         * Participate in the award to receive badge and tokens.
         *
         * @param req Use {@link Build5Request} with data based on {@link AwardParticpateRequest}
         * @returns
         */
        this.participate = (req) => this.sendRequest(interfaces_1.WEN_FUNC.participateAward)(req);
        /**
         * Approve participants and distribute them with token and NFT
         *
         * @param req Use {@link Build5Request} with data based on {@link AwardApproveParticipantRequest}
         * @returns
         */
        this.approveParticipant = (req) => this.sendRequest(interfaces_1.WEN_FUNC.approveParticipantAward)(req);
        /**
         * Cancel ongoing award and get refunded with remaining tokens.
         *
         * @param req Use {@link Build5Request} with data based on {@link AwardCancelRequest}
         * @returns
         */
        this.cancel = (req) => this.sendRequest(interfaces_1.WEN_FUNC.cancelAward)(req);
        /**
         * Helper GET function to get "active" awards per space. Returns observable with continues updates via Websocket.
         *
         * @param space
         * @param startAfter
         * @returns
         */
        this.getActiveLive = (space, startAfter) => {
            const params = {
                dataset: this.dataset,
                fieldName: ['space', 'endDate', 'completed', 'approved'],
                fieldValue: [space, new Date().toISOString(), false, true],
                operator: [interfaces_1.Opr.EQUAL, interfaces_1.Opr.GREATER_OR_EQUAL, interfaces_1.Opr.EQUAL, interfaces_1.Opr.EQUAL],
                startAfter,
            };
            return this.getManyAdvancedLive(params);
        };
        /**
         * Helper GET function to get "completed" awards per space. Returns observable with continues updates via Websocket.
         *
         * @param space
         * @param startAfter
         * @returns
         */
        this.getCompletedLive = (space, startAfter) => {
            const params = {
                dataset: this.dataset,
                fieldName: ['space', 'completed', 'approved'],
                fieldValue: [space, true, true],
                operator: [interfaces_1.Opr.EQUAL, interfaces_1.Opr.EQUAL, interfaces_1.Opr.EQUAL],
                startAfter,
            };
            return this.getManyAdvancedLive(params);
        };
        /**
         * Helper GET function to get "draft" awards per space. Returns observable with continues updates via Websocket.
         *
         * @param space
         * @param startAfter
         * @returns
         */
        this.getDraftLive = (space, startAfter) => {
            const params = {
                dataset: this.dataset,
                fieldName: ['space', 'endDate', 'rejected', 'approved'],
                fieldValue: [space, new Date().toISOString(), false, false],
                operator: [interfaces_1.Opr.EQUAL, interfaces_1.Opr.GREATER_OR_EQUAL, interfaces_1.Opr.EQUAL, interfaces_1.Opr.EQUAL],
                startAfter,
            };
            return this.getManyAdvancedLive(params);
        };
        /**
         * Helper GET function to get "rejected" awards per space. Returns observable with continues updates via Websocket.
         *
         * @param space
         * @param startAfter
         * @returns
         */
        this.getRejectedLive = (space, startAfter) => {
            const params = {
                dataset: this.dataset,
                fieldName: ['space', 'rejected'],
                fieldValue: [space, false],
                operator: [interfaces_1.Opr.EQUAL, interfaces_1.Opr.EQUAL],
                startAfter,
            };
            return this.getManyAdvancedLive(params);
        };
        /**
         * Helper GET function to get closest finishing awards. Returns observable with continues updates via Websocket.
         *
         * @param startAfter
         * @returns
         */
        this.getLastActiveLive = (startAfter) => {
            const fieldName = ['endDate', 'completed', 'approved'];
            const fieldValue = [new Date().toISOString(), false, true];
            const operator = [interfaces_1.Opr.GREATER_OR_EQUAL, interfaces_1.Opr.EQUAL, interfaces_1.Opr.EQUAL];
            const orderBy = ['endDate'];
            const params = {
                dataset: this.dataset,
                fieldName,
                fieldValue,
                operator,
                startAfter,
                orderBy,
            };
            return this.getManyAdvancedLive(params);
        };
        /**
         * Helper GET Award participants. Returns observable with continues updates via Websocket.
         *
         * @param startAfter
         * @returns
         */
        this.getTopByMemberLive = (member, completed, startAfter) => {
            const members = this.subset(interfaces_1.Subset.PARTICIPANTS).getTopByMemberLive(member, completed, startAfter);
            return members.pipe((0, rxjs_1.switchMap)(async (members) => {
                const promises = members.map((member) => this.id(member.parentId).get());
                return (await Promise.all(promises)).map((s) => s);
            }));
        };
    }
}
exports.AwardDataset = AwardDataset;
